/**
 * Integrytas Group - Common Security Module (CSM)
 * Forgot-password screen. Triggers a Supabase reset email.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Watermark } from '../common/Watermark';
import { APPLICATION_NAME, BRANDING_SUFFIX } from '../../constants/branding';

export interface ForgotPasswordScreenProps {
  onRequestReset: (email: string) => Promise<void>;
  onBackToSignIn: () => void;
  errorMessage?: string | null;
}

export function ForgotPasswordScreen({
  onRequestReset, onBackToSignIn, errorMessage,
}: ForgotPasswordScreenProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [busy, setBusy]   = useState(false);
  const [sent, setSent]   = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onRequestReset(email);
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex h-screen w-screen items-center justify-center bg-csm-surface-alt">
      <Watermark appName={APPLICATION_NAME} brandingSuffix={BRANDING_SUFFIX} />

      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded border border-csm-border bg-white p-6 shadow-sm"
      >
        <h1 className="mb-1 text-csm-lg font-semibold text-csm-ink">{t('auth.forgot_password_screen.title')}</h1>
        <p className="mb-4 text-csm-sm text-csm-muted">
          {t('auth.forgot_password_screen.subtitle')}
        </p>

        {sent ? (
          <>
            <div className="mb-3 rounded border border-csm-success-border bg-csm-success-soft px-2 py-2 text-csm-sm text-csm-success">
              {t('auth.forgot_password_screen.sent_message', { email })}
            </div>
            <button
              type="button" onClick={onBackToSignIn}
              className="h-8 w-full rounded border border-primary bg-primary text-csm-md text-white hover:bg-primary-hover"
            >
              {t('auth.forgot_password_screen.back_to_sign_in')}
            </button>
          </>
        ) : (
          <>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.forgot_password_screen.email_label')}</label>
            <input
              type="email" required autoComplete="email"
              value={email} onChange={e => setEmail(e.target.value)}
              className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
            />

            {errorMessage && (
              <div className="mb-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">
                {errorMessage}
              </div>
            )}

            <button
              type="submit" disabled={busy}
              className="mb-3 h-8 w-full rounded border border-primary bg-primary text-csm-md text-white hover:bg-primary-hover disabled:opacity-60"
            >
              {busy ? t('auth.forgot_password_screen.submitting') : t('auth.forgot_password_screen.submit')}
            </button>
            <button
              type="button" onClick={onBackToSignIn}
              className="h-8 w-full rounded border border-csm-border bg-white text-csm-sm text-csm-ink-soft hover:bg-csm-surface-alt"
            >
              {t('auth.forgot_password_screen.back_to_sign_in')}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
